/**
 * WorkoutLogPanel — inline workout logging panel that slides in below the AI coach message list.
 *
 * Data flow:
 *  1. Fetch GET /api/lanista/plan → exercise list + targets
 *  2. Fetch GET /api/workouts/free/today → pre-fill from today (if exists)
 *  3. If 404 / null, fetch GET /api/workouts/free/last → pre-fill from last session
 *  4. POST /api/workouts/free on save → calls onSave(prs) with detected PRs
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useLanistaPlan, useTodayFreeWorkout, useLastFreeWorkout } from "../../api/hooks";
import { apiFetch } from "../../api/client";
import { writeErrorLine } from "../../lib/writeError";
import type { FreeWeightWorkout, LanistaExercise, PRHit } from "../../api/types";
import { useUnits } from "../../lib/UnitsProvider";
import { toKg, toLb, type LoadUnit } from "../../lib/units";
import { WEIGHT_STEP } from "../../lib/weightStep";
import { MuscleGroupAvatar } from "./index";
import { TOKENS } from "../ui/dashboard-primitives";
import { AsyncPanel, PanelSkeleton, Shimmer } from "../ui/AsyncPanel";
import { unreachable } from "../../lib/queryState";
import { track } from "../../analytics";

const CREAM = TOKENS.cream;
const CREAM_SOFT = TOKENS.creamSoft;
const CREAM_DIM = TOKENS.creamDim;
const CREAM_FAINT = TOKENS.creamFaint;
const AMBER = TOKENS.amber;
// eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
const AMBER_BORDER = "rgba(232, 197, 160, 0.38)";
const TERRACOTTA = TOKENS.terracotta;

interface SetState {
  weight_kg: number | null;
  reps: number | null;
  // metric/duration_sec/*_right round-trip through this component (Task 3-6
  // wired the data path). SetRow (below) is where authoring lives — the
  // duration input when metric is "seconds", and the left/right split.
  metric: "reps" | "seconds";
  duration_sec: number | null;
  weight_kg_right: number | null;
  reps_right: number | null;
  duration_sec_right: number | null;
}

/** The three fields a stepper button can adjust. Right-side fields are typed
 * only, no steppers -- the split is the exception case and stays compact. */
type PrimaryField = "weight_kg" | "reps" | "duration_sec";
type SetField = PrimaryField | "weight_kg_right" | "reps_right" | "duration_sec_right";

type RecordedValueFields = Pick<
  SetState,
  "weight_kg" | "reps" | "duration_sec" | "weight_kg_right" | "reps_right" | "duration_sec_right"
>;

/** Mirrors has_recorded_value (api/free_weight_log.py): true if *s* carries a
 * value on any of the six columns the backend checks before deciding whether
 * to write the row at all. A row that fails this is skipped by
 * upsert_workout_sets -- never persisted -- so it must be treated as "will
 * not be submitted" everywhere on this side too: excluded from the POST body
 * (handleSave) and excluded from validation (needsBaseDuration), or an
 * empty row the user never touched would either block Save with a message
 * about nothing, or -- worse -- reach the backend as metric:"seconds" with no
 * duration_sec and 422 the whole day (see needsBaseDuration below). */
function hasRecordedValue(s: RecordedValueFields): boolean {
  return (
    s.weight_kg != null ||
    s.reps != null ||
    s.duration_sec != null ||
    s.weight_kg_right != null ||
    s.reps_right != null ||
    s.duration_sec_right != null
  );
}

/** Mirrors ck_free_weight_sets_shape (api/models.py): the base duration_sec
 * is required whenever metric is "seconds". The constraint is unconditional
 * -- it fires on ANY metric:"seconds" row with no duration_sec, not just a
 * right-only split -- so this must catch every shape that reaches the
 * server with that combination, not only duration_sec_right != null. It must
 * NOT catch a row hasRecordedValue() says is empty: that row is never
 * submitted (see handleSave), so flagging it here would block Save over a
 * set the user never touched and that was never going to be sent. */
function needsBaseDuration(s: RecordedValueFields & Pick<SetState, "metric">): boolean {
  return s.metric === "seconds" && s.duration_sec == null && hasRecordedValue(s);
}

const BLANK_SET: SetState = {
  weight_kg: null,
  reps: null,
  metric: "reps",
  duration_sec: null,
  weight_kg_right: null,
  reps_right: null,
  duration_sec_right: null,
};

interface ExerciseState {
  sets: SetState[];
}

// ── Unit-aware weight input (Task 7, D7) ────────────────────────────────────
//
// `SetState.weight_kg` and `weight_kg_right` stay canonical kilograms at
// every point in this component's state -- the prefill effect above already
// seeds them straight from the API's kg, unconverted, and handleSave sends
// them straight back out, unconverted. The three weight NumFields below are
// the ONLY place a unit conversion happens, at the render/onChange boundary:
// `weightForDisplay` converts stored kg to what the field shows, and the
// stepper/typed-input handlers convert back through `toKg` before ever
// touching state. Nothing here ever seeds a piece of state FROM the rounded
// display value -- that is exactly the bug D7 exists to prevent (82.5 kg
// rendered as 182 lb must never round-trip back to 82.554 kg just because the
// panel was opened and saved untouched).

/** Stored kg -> the number a weight NumField shows. `kg` passes through
 *  unrounded (existing behaviour); `lb` rounds to the nearest whole pound —
 *  D4's own rendering rule, and the only precision a 5 lb step can land on.
 *  `null` (nothing recorded) passes through untouched in either unit. */
function weightForDisplay(kg: number | null, unit: LoadUnit): number | null {
  if (kg == null) return null;
  return unit === "lb" ? Math.round(toLb(kg)) : kg;
}

interface WorkoutLogPanelProps {
  show: boolean;
  onClose: () => void;
  onSave: (prs: PRHit[]) => void;
}

export function WorkoutLogPanel({ show, onClose, onSave }: WorkoutLogPanelProps) {
  const { formatLoad, load } = useUnits();
  const planQuery = useLanistaPlan();
  const plan = planQuery.data;
  const todayQuery = useTodayFreeWorkout();
  // The one source in the app where a cached answer does NOT win, and the
  // exception is the point: Save replaces all of today's sets, so the form may
  // only be built from an answer fetched during THIS open. The query refetches
  // on every open (`staleTime: 0`), but its cache lives 24 h, so a second open
  // holds whatever the first one fetched. Drawing from that, or keeping it when
  // the refetch fails, would save sets logged on the phone in between back to
  // their older values. `isFetchedAfterMount` is what makes "this open"
  // testable; `todayWorkout` stays `undefined` until then, which is what the
  // init effect and `hasContent` below already read as "no answer yet"
  // (verification pass on #172).
  const todayFresh =
    todayQuery.isSuccess && todayQuery.isFetchedAfterMount && !todayQuery.isFetching;
  const todayWorkout = todayFresh ? todayQuery.data : undefined;
  // A fetch in flight is a wait even when the cache holds something, so the
  // panel skeletons rather than drawing the old answer for a frame.
  const todaySource = { ...todayQuery, isLoading: todayQuery.isFetching };
  const lastQuery = useLastFreeWorkout();
  const { data: lastWorkout, isPending: lastPending } = lastQuery;
  // "There is none" is read from an answer fetched during THIS open, never from the cache: a cached 404 outlives its
  // day (a tab kept open past midnight, a workout logged on the phone since) and would make a later workout "the
  // first" again. Same test as today's above; the prefill may still come from the cached answer (it only fills fields).
  const lastFresh = lastQuery.isSuccess && lastQuery.isFetchedAfterMount && !lastQuery.isFetching;

  const [exerciseStates, setExerciseStates] = useState<Record<string, ExerciseState>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize set state from today or last workout once plan + BOTH workout
  // queries have settled. The three queries are independent — resolving this
  // as soon as `plan` lands (or on `todayWorkout` alone) is a genuine race: if
  // `todayWorkout` hasn't come back yet, prefill silently falls back to
  // `null`/`lastWorkout` and a real live-logged set (duration, side) never
  // makes it into state before the effect latches `initialized`. So init only
  // runs once each has resolved to either a workout or null.
  //
  // Today's must have ANSWERED, not merely settled. Save replaces all of
  // today's sets, so a form built while today's request had failed, prefilled
  // from the previous day, could save over sets that were logged and merely did
  // not arrive. `undefined` is "no answer yet" (pending or failed); `null` is
  // "nothing logged today". The panel shows the house error in that case, and
  // this effect runs once Try again brings the answer (D5 review). The previous
  // workout is only a prefill, so its failure just drops the prefill.
  useEffect(() => {
    if (!plan || initialized || todayWorkout === undefined || lastPending) return;

    // Only today's own workout is a same-day, actually-performed source for
    // side (weight_kg_right/reps_right/duration_sec_right). Falling back to
    // `lastWorkout` for weight/reps is fine — the inputs show those and the
    // user can see and correct them — but there is no UI for the split-side
    // columns unless "Split sides" is opened, so copying them forward from a
    // *previous day's* set would silently invent today's side split with
    // nothing on screen to catch it unless the user happens to open the
    // reveal. Reset those three to null whenever the prefill source is not
    // today. metric and duration_sec do NOT get this treatment (item 3 fix,
    // see below) — both drive a primary, always-visible input exactly like
    // weight_kg/reps, so the same "the user can see and correct it" argument
    // that already justifies carrying weight/reps forward applies to them
    // too.
    const prefill: FreeWeightWorkout | null = todayWorkout ?? lastWorkout ?? null;
    const prefillIsToday = todayWorkout != null;

    const initial: Record<string, ExerciseState> = {};

    for (const ex of plan.exercises) {
      const priorSets = prefill?.sets.filter((s) => s.exercise_name === ex.name) ?? [];

      const defaultWeight =
        priorSets.length > 0
          ? priorSets[0].weight_kg
          : ex.target_weight_kg ?? null;

      const defaultReps =
        priorSets.length > 0
          ? priorSets[0].reps
          : ex.target_reps_min ?? null;

      // A sibling set already logged for this exercise -- today, or (item 3
      // fix) on the prefill source's day, whichever `priorSets` holds -- is
      // the only signal this panel has for what dimension scores the set.
      // The plan itself carries no classification field (LanistaExerciseOut
      // has none; closing that needs a backend field, out of scope here).
      // Inherit it for a target-set slot beyond what `priorSets` already
      // covers, rather than defaulting to "reps": a hold's set 2/3, opened
      // here before it has its own row, must not silently save as
      // metric='reps' (Task 7 review finding) -- and neither must EVERY set
      // of an exercise that is a hold on every day it has ever been logged,
      // just because none of them happened to be logged today (item 3
      // finding: with `todayWorkout` null, this used to force "reps" on the
      // whole exercise regardless of `priorSets`, fabricating a `target_reps_
      // min` rep count under a metric that cannot be performed in reps).
      // Carry a starting duration_sec along with the metric, from the same
      // sibling -- a real metric='seconds' set always has a non-null
      // duration_sec (ck_free_weight_sets_shape guarantees it once the row is
      // backend data), so an inherited "seconds" slot is never left with
      // nothing to show in its own Hold (sec) field.
      const holdSibling = priorSets.find((p) => p.metric === "seconds");
      const inheritedMetric: SetState["metric"] = holdSibling ? "seconds" : "reps";
      const inheritedDuration = holdSibling?.duration_sec ?? null;

      const targetSetCount = ex.target_sets ?? (priorSets.length || 3);

      const sets: SetState[] = [];
      for (let i = 0; i < targetSetCount; i++) {
        const src = priorSets[i];
        // Not gated on prefillIsToday (item 3 fix): metric drives which
        // primary input renders (Hold (sec) vs. Reps) and duration_sec is
        // that input's own value, so both are exactly as visible and
        // correctable as weight_kg/reps already are below -- there is no
        // reason to fabricate reps for these two where weight/reps get the
        // real prior value.
        const metric: SetState["metric"] = src?.metric ?? inheritedMetric;
        sets.push({
          weight_kg: src?.weight_kg ?? defaultWeight,
          reps: src?.reps ?? defaultReps,
          metric,
          duration_sec: src?.duration_sec ?? (metric === "seconds" ? inheritedDuration : null),
          // The three split-side fields stay today-only (see the comment on
          // `prefill` above) -- they have no always-visible input to carry
          // the correction, unlike metric/duration_sec.
          weight_kg_right: prefillIsToday ? src?.weight_kg_right ?? null : null,
          reps_right: prefillIsToday ? src?.reps_right ?? null : null,
          duration_sec_right: prefillIsToday ? src?.duration_sec_right ?? null : null,
        });
      }
      initial[ex.name] = { sets };
    }

    setExerciseStates(initial);
    setInitialized(true);
  }, [plan, todayWorkout, lastWorkout, lastPending, initialized]);

  const adjust = (
    exerciseName: string,
    setIdx: number,
    field: PrimaryField,
    delta: number
  ) => {
    setExerciseStates((prev) => {
      const ex = { ...prev[exerciseName] };
      const sets = [...ex.sets];
      const s = { ...sets[setIdx] };
      if (field === "weight_kg") {
        // `delta` arrives already in the DISPLAY unit (±2.5 kg or ±5 lb, per
        // SetRow's AdjustButton) -- step off the value the user actually
        // sees, not the stored kg, or a pound user's "+5" press would
        // silently move the field by 5 KG instead. This is a real edit (a
        // tap), so converting the resulting display value back through
        // toKg -- and requantizing the stored kg to whatever a whole pound
        // maps to -- is exactly what D7 allows.
        const curDisplay = weightForDisplay(s.weight_kg, load) ?? 0;
        const newDisplay = Math.max(0, curDisplay + delta);
        s.weight_kg = load === "lb" ? toKg(newDisplay) : Math.round(newDisplay * 10) / 10;
      } else {
        const cur = (s[field] ?? 0) as number;
        s[field] = Math.max(0, Math.round((cur + delta) * 10) / 10) as never;
      }
      sets[setIdx] = s;
      return { ...prev, [exerciseName]: { ...ex, sets } };
    });
  };

  const addSet = (exerciseName: string) => {
    setExerciseStates((prev) => {
      const ex = prev[exerciseName];
      const last = ex.sets[ex.sets.length - 1] ?? BLANK_SET;
      // Copy weight/reps/metric forward (all visible in the inputs, user can
      // correct them) but never the three right-side fields — a set that was
      // just added has not been performed, so it has no real side split to
      // carry, and inheriting one would auto-reveal a split this new row was
      // never given data for. Carrying `metric` matters on its own: without
      // it, "+ Add set" on a hold exercise defaulted its new row to reps and
      // the Hold (sec) field silently became a Reps field (Task 7 review
      // finding). duration_sec follows metric for the same reason
      // weight_kg/reps already carry forward here — the field opens with a
      // real starting value instead of an empty one that would fail
      // ck_free_weight_sets_shape ("seconds" with no duration) if saved
      // untouched.
      return {
        ...prev,
        [exerciseName]: {
          sets: [
            ...ex.sets,
            {
              ...BLANK_SET,
              weight_kg: last.weight_kg,
              reps: last.reps,
              metric: last.metric,
              duration_sec: last.metric === "seconds" ? last.duration_sec : null,
            },
          ],
        },
      };
    });
  };

  const setField = (
    exerciseName: string,
    setIdx: number,
    field: SetField,
    raw: string
  ) => {
    const parsed = raw === "" ? null : parseFloat(raw);
    const isWeightField = field === "weight_kg" || field === "weight_kg_right";
    // `raw` is a real keystroke from the field the user is looking at, i.e.
    // already in the DISPLAY unit -- toKg here is the one-time, on-submit
    // conversion D7 requires, never applied to a value the user didn't type.
    const num =
      parsed === null || Number.isNaN(parsed)
        ? null
        : isWeightField && load === "lb"
          ? toKg(parsed)
          : parsed;
    setExerciseStates((prev) => {
      const ex = { ...prev[exerciseName] };
      const sets = [...ex.sets];
      sets[setIdx] = { ...sets[setIdx], [field]: num };
      return { ...prev, [exerciseName]: { ...ex, sets } };
    });
  };

  // A timed set left with no base duration_sec (right-only split, or any
  // other metric:"seconds" row missing it) would 422 at the database — see
  // needsBaseDuration. Computed on every render off live state so the Save
  // button (and the inline message in SetRow) react the moment a hold is
  // left incomplete. A row needsBaseDuration itself excludes as empty is not
  // counted here either, for the same reason: handleSave never sends it.
  const hasValidationError = Object.values(exerciseStates).some((ex) =>
    ex.sets.some(needsBaseDuration)
  );

  const handleSave = async () => {
    // Gating the prefill effect on the workout queries settling (above) opens
    // a window where `plan` has resolved but `exerciseStates` is still `{}`.
    // Without this guard a click in that window would iterate `plan.exercises`
    // and `continue` past every one of them (`!exState`), POSTing `sets: []` —
    // replace-all-sets strategy means that wipes the whole day, worse than any
    // single wrong field. Block Save until prefill has actually landed.
    if (!plan || !initialized) return;
    // Belt-and-suspenders: the Save button is already disabled while this is
    // true, but a direct call shouldn't be able to slip a 422-doomed set
    // past the check that exists to stop it in the form.
    if (hasValidationError) return;
    setSaveError(null);
    setIsSaving(true);

    const allSets: Array<{
      exercise_name: string;
      position: number;
      set_number: number;
      weight_kg: number | null;
      reps: number | null;
      metric: "reps" | "seconds";
      duration_sec: number | null;
      weight_kg_right: number | null;
      reps_right: number | null;
      duration_sec_right: number | null;
    }> = [];

    for (const ex of plan.exercises) {
      const exState = exerciseStates[ex.name];
      if (!exState) continue;
      for (let si = 0; si < exState.sets.length; si++) {
        const s = exState.sets[si];
        // A row recording nothing at all is exactly what upsert_workout_sets
        // (api/free_weight_log.py) skips on the way in -- has_recorded_value
        // there returns false and it is never written. Skip it here too,
        // client-side: SetIn._check_shape validates metric:"seconds" needing
        // duration_sec BEFORE has_recorded_value ever runs, so an empty
        // inherited-hold row (metric:"seconds", duration_sec: null, nothing
        // else filled -- e.g. an un-logged hold set the user cleared rather
        // than performed) would 422 the ENTIRE payload if sent, taking every
        // other exercise's real sets down with it. Not sending it in the
        // first place is what keeps that row's emptiness a no-op rather than
        // a save failure. set_number stays si + 1 either way -- gaps from a
        // skipped middle set are already normal (the backend has always
        // filtered these out after receiving them).
        if (!hasRecordedValue(s)) continue;
        allSets.push({
          exercise_name: ex.name,
          position: ex.position,
          set_number: si + 1,
          weight_kg: s.weight_kg,
          reps: s.reps,
          metric: s.metric,
          duration_sec: s.duration_sec,
          weight_kg_right: s.weight_kg_right,
          reps_right: s.reps_right,
          duration_sec_right: s.duration_sec_right,
        });
      }
    }

    try {
      const today = new Date().toISOString().split("T")[0];
      const result = await apiFetch<FreeWeightWorkout>("/api/workouts/free", {
        method: "POST",
        body: JSON.stringify({ date: today, sets: allSets }),
      });
      // Analytics (#708): the count of sets sent is the one number reported, never a weight or a repetition. "First" is
      // an answer of 404 (`null`; an error or no answer is `undefined` and is not "none") from `GET /workouts/free/last`
      // read during this open (`lastFresh`), with nothing logged today yet: that route reads days before today, so a
      // second save of the first day's workout is told from the first by what today already holds. A save of no sets is
      // not a logged workout.
      track("workout_finish", { set_count: allSets.length });
      if (allSets.length > 0 && lastFresh && lastWorkout === null && (todayWorkout?.sets.length ?? 0) === 0) {
        track("first_workout_saved");
      }
      onSave(result.prs ?? []);
    } catch (err) {
      setSaveError(writeErrorLine(err, "Save failed. Please try again."));
    } finally {
      setIsSaving(false);
    }
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        // No entrance of its own: the panel is drawn inside the workout-log dialog, whose own entrance
        // already plays, and two stacked rises read as one stutter (Apple design review SH-3).
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
        style={{
          background: TOKENS.creamHairline,
          borderTop: `1px solid ${TOKENS.creamBorder}`,
          maxHeight: "480px",
          overflowY: "auto",
          fontFamily: "var(--font-body)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 pt-3 pb-2"
          style={{ borderBottom: "1px solid " + TOKENS.creamHairline }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "10px",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              color: CREAM_DIM,
            }}
          >
            Log workout
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-xs transition-opacity duration-150 hover:opacity-100 outline-none"
            style={{ color: CREAM_DIM, opacity: 0.7 }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-3 space-y-4">
          {/* A failed plan request used to read "No active plan found" too:
              the hook turned every error into null (Spec 2 D5, #172). Today's
              workout is a source as well: the form is not drawn without its
              answer, because saving it would replace today's sets. */}
          <AsyncPanel
            sources={[planQuery, todaySource]}
            hasContent={plan != null && todayFresh}
            subject={unreachable(planQuery) ? "Your training plan" : "Today's workout"}
            skeleton={
              <PanelSkeleton label="Loading your training plan">
                <div className="space-y-3">
                  <Shimmer height={14} width={120} radius={4} />
                  <Shimmer height={96} />
                  <Shimmer height={96} />
                </div>
              </PanelSkeleton>
            }
            empty={
              <p style={{ color: CREAM_DIM, fontSize: "13px" }}>
                No active plan found.
              </p>
            }
          >
            {() => plan && (
            <>
              {/* Plan name */}
              <p
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "11px",
                  color: AMBER,
                  letterSpacing: "0.08em",
                }}
              >
                {plan.name}
              </p>

              {/* Exercise cards */}
              {plan.exercises.map((ex: LanistaExercise) => {
                const state = exerciseStates[ex.name];
                if (!state) return null;
                const targetLabel = buildTargetLabel(ex, formatLoad);
                return (
                  <ExerciseCard
                    key={ex.name}
                    exercise={ex}
                    targetLabel={targetLabel}
                    state={state}
                    onAdjust={(setIdx, field, delta) => adjust(ex.name, setIdx, field, delta)}
                    onSetField={(setIdx, field, raw) => setField(ex.name, setIdx, field, raw)}
                    onAddSet={() => addSet(ex.name)}
                  />
                );
              })}
            </>
            )}
          </AsyncPanel>
        </div>

        {/* Bottom bar */}
        {plan && (
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderTop: "1px solid " + TOKENS.creamHairline }}
          >
            {saveError && (
              <span style={{ fontSize: "12px", color: TERRACOTTA }}>{saveError}</span>
            )}
            <div className="flex items-center gap-4 ml-auto">
              <button
                type="button"
                onClick={onClose}
                style={{ color: CREAM_DIM, fontSize: "12px" }}
                className="hover:opacity-80 transition-opacity outline-none"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={isSaving || !initialized || hasValidationError}
                className="rounded-xl px-5 py-2 text-xs transition-all duration-150 outline-none focus-visible:ring-2 disabled:opacity-50"
                style={{
                  background: AMBER,
                  color: "#1c1816",
                  fontFamily: "var(--font-mono)",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  boxShadow: "0 4px 14px -4px " + TOKENS.amberEmphasis,
                }}
              >
                {isSaving ? "Saving…" : "Save workout"}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildTargetLabel(ex: LanistaExercise, formatLoad: (n: number) => string): string {
  if (!ex.target_sets) return "";
  const reps =
    ex.target_reps_min && ex.target_reps_max
      ? `${ex.target_reps_min}–${ex.target_reps_max}`
      : String(ex.target_reps_min ?? ex.target_reps_max ?? "?");
  const weight = ex.target_weight_kg ? ` @ ${formatLoad(ex.target_weight_kg)}` : "";
  return `${ex.target_sets}×${reps}${weight}`;
}

// ── ExerciseCard sub-component ───────────────────────────────────────────────

interface ExerciseCardProps {
  exercise: LanistaExercise;
  targetLabel: string;
  state: ExerciseState;
  onAdjust: (setIdx: number, field: PrimaryField, delta: number) => void;
  onSetField: (setIdx: number, field: SetField, raw: string) => void;
  onAddSet: () => void;
}

function ExerciseCard({
  exercise,
  targetLabel,
  state,
  onAdjust,
  onSetField,
  onAddSet,
}: ExerciseCardProps) {
  return (
    <div
      className="rounded-xl p-3"
      style={{
        background: TOKENS.creamHairline,
        border: `1px solid ${TOKENS.panelBorderDark}`,
      }}
    >
      {/* Exercise header */}
      <div className="flex items-center gap-2.5 mb-2.5">
        <MuscleGroupAvatar muscleGroup={exercise.muscle_group} size="sm" />
        <div className="flex-1 min-w-0">
          <p
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "11px",
              color: CREAM,
              fontVariant: "small-caps",
              letterSpacing: "0.06em",
              lineHeight: 1.2,
            }}
          >
            {exercise.name}
          </p>
          {targetLabel && (
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "10px",
                color: CREAM_DIM,
                marginTop: "2px",
              }}
            >
              {targetLabel}
            </p>
          )}
        </div>
      </div>

      {/* Set rows */}
      <div className="space-y-1.5">
        {state.sets.map((s, si) => (
          <SetRow
            key={si}
            setNumber={si + 1}
            set={s}
            onAdjust={(field, delta) => onAdjust(si, field, delta)}
            onSetField={(field, raw) => onSetField(si, field, raw)}
          />
        ))}
      </div>

      {/* Add set */}
      <button
        type="button"
        onClick={onAddSet}
        className="mt-2 text-xs transition-opacity duration-150 hover:opacity-90 outline-none"
        style={{
          color: AMBER,
          fontFamily: "var(--font-mono)",
          letterSpacing: "0.06em",
          opacity: 0.75,
        }}
      >
        + Add set
      </button>
    </div>
  );
}

// ── SetRow sub-component ─────────────────────────────────────────────────────

interface SetRowProps {
  setNumber: number;
  set: SetState;
  onAdjust: (field: PrimaryField, delta: number) => void;
  onSetField: (field: SetField, raw: string) => void;
}

const inputStyle: React.CSSProperties = {
  background: TOKENS.creamHairline,
  border: "1px solid " + TOKENS.creamBorder,
  borderRadius: "8px",
  color: CREAM,
  fontFamily: "var(--font-mono)",
  // fontSize is a class on the input (16px on touch, 13px under a mouse): under 16px iOS Safari
  // zooms the page on focus, and an inline style cannot say pointer-fine.
  textAlign: "center",
  outline: "none",
  width: "56px",
  height: "32px",
  padding: "0 4px",
};

const adjustBtnStyle: React.CSSProperties = {
  color: CREAM_FAINT,
  fontFamily: "var(--font-mono)",
  fontSize: "14px",
  width: "26px",
  height: "26px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "6px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  transition: "color 0.12s, background 0.12s",
};

const sideTagStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "9px",
  color: CREAM_FAINT,
  width: "14px",
  flexShrink: 0,
  letterSpacing: "0.06em",
};

// Label for a leftover `reps` field surfaced on a metric:"seconds" row (see
// the reps-recovery block in SetRow below). Distinct from sideTagStyle
// because "reps" is a word, not a single L/R character, so it can't share
// that fixed 14px width.
const inheritedTagStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "9px",
  color: CREAM_FAINT,
  flexShrink: 0,
  letterSpacing: "0.06em",
};

const timesSignStyle: React.CSSProperties = {
  color: CREAM_FAINT,
  fontSize: "10px",
  fontFamily: "var(--font-mono)",
};

// Shared by both branches of SetRow's missing-base-duration message -- the
// split branch had this inline before; the unsplit branch (item 1 fix) needs
// the identical styling, so it is factored out rather than duplicated.
const missingDurationMessageStyle: React.CSSProperties = {
  color: TERRACOTTA,
  fontSize: "10px",
  fontFamily: "var(--font-mono)",
  marginTop: "1px",
};

function AdjustButton({
  label,
  ariaLabel,
  onClick,
}: {
  label: string;
  ariaLabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      style={adjustBtnStyle}
      onClick={onClick}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.color = CREAM_SOFT;
        (e.currentTarget as HTMLElement).style.background = TOKENS.creamHairline;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.color = CREAM_FAINT;
        (e.currentTarget as HTMLElement).style.background = "transparent";
      }}
      aria-label={ariaLabel}
    >
      {label}
    </button>
  );
}

function NumberField({
  ariaLabel,
  value,
  placeholder,
  step,
  onChange,
}: {
  ariaLabel: string;
  value: number | null;
  placeholder: string;
  step: string;
  onChange: (raw: string) => void;
}) {
  return (
    <input
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="text-base pointer-fine:text-[13px]"
      style={inputStyle}
      placeholder={placeholder}
      step={step}
      min="0"
      aria-label={ariaLabel}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = AMBER_BORDER;
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = TOKENS.creamBorder;
      }}
    />
  );
}

function SetRow({ setNumber, set, onAdjust, onSetField }: SetRowProps) {
  const { load } = useUnits();
  const weightStep = WEIGHT_STEP[load];
  // Purely local, UI-only reveal: does not affect what gets saved. A set
  // whose right side already carries a value opens split on its own — the
  // reveal control only covers the case where the user is starting a new
  // split from a plain set. Once opened it stays open for this row: closing
  // it again after typing a right-side value would make the split look
  // undone without actually clearing the data underneath it.
  const [manualSplit, setManualSplit] = useState(false);
  const isSeconds = set.metric === "seconds";
  const hasRightData =
    set.weight_kg_right != null || set.reps_right != null || set.duration_sec_right != null;
  const isSplit = hasRightData || manualSplit;
  const missingBaseDuration = needsBaseDuration(set);

  return (
    <div className="flex items-start gap-2">
      {/* Set label */}
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "10px",
          color: CREAM_FAINT,
          width: "28px",
          flexShrink: 0,
          letterSpacing: "0.1em",
          paddingTop: "6px",
        }}
      >
        S{setNumber}
      </span>

      {!isSplit ? (
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Weight group */}
            <div className="flex items-center gap-1">
              <AdjustButton
                label="−"
                ariaLabel={`Decrease weight by ${weightStep} ${load}`}
                onClick={() => onAdjust("weight_kg", -weightStep)}
              />
              <NumberField
                ariaLabel={`Weight in ${load}`}
                value={weightForDisplay(set.weight_kg, load)}
                placeholder={load}
                step={String(weightStep)}
                onChange={(raw) => onSetField("weight_kg", raw)}
              />
              <AdjustButton
                label="+"
                ariaLabel={`Increase weight by ${weightStep} ${load}`}
                onClick={() => onAdjust("weight_kg", weightStep)}
              />
            </div>

            <span style={timesSignStyle}>×</span>

            {/* Reps or hold group, depending on metric */}
            {isSeconds ? (
              <>
                <div className="flex items-center gap-1">
                  <AdjustButton
                    label="−"
                    ariaLabel="Decrease hold by 5 seconds"
                    onClick={() => onAdjust("duration_sec", -5)}
                  />
                  <NumberField
                    ariaLabel="Hold (sec)"
                    value={set.duration_sec}
                    placeholder="sec"
                    step="1"
                    onChange={(raw) => onSetField("duration_sec", raw)}
                  />
                  <AdjustButton
                    label="+"
                    ariaLabel="Increase hold by 5 seconds"
                    onClick={() => onAdjust("duration_sec", 5)}
                  />
                </div>
                {/* A seconds row has no other rendered input for `reps`, but
                    a row can still carry one -- inherited from a
                    mixed-metric history (see hasRecordedValue/
                    needsBaseDuration above). hasRecordedValue counts it, so
                    without this it could never be seen or cleared and Save
                    would stay blocked forever. Only rendered when the value
                    is actually present: a plain seconds row looks exactly
                    as it did before. */}
                {set.reps != null && (
                  <div className="flex items-center gap-1">
                    <span style={inheritedTagStyle}>reps</span>
                    <NumberField
                      ariaLabel="Reps (inherited)"
                      value={set.reps}
                      placeholder="reps"
                      step="1"
                      onChange={(raw) => onSetField("reps", raw)}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-1">
                <AdjustButton
                  label="−"
                  ariaLabel="Decrease reps by 1"
                  onClick={() => onAdjust("reps", -1)}
                />
                <NumberField
                  ariaLabel="Reps"
                  value={set.reps}
                  placeholder="reps"
                  step="1"
                  onChange={(raw) => onSetField("reps", raw)}
                />
                <AdjustButton
                  label="+"
                  ariaLabel="Increase reps by 1"
                  onClick={() => onAdjust("reps", 1)}
                />
              </div>
            )}

            {/* Reveal control -- unobtrusive on purpose: a split set is the
                exception, not the default. */}
            <button
              type="button"
              onClick={() => setManualSplit(true)}
              className="text-xs transition-opacity duration-150 hover:opacity-90 outline-none"
              style={{
                color: AMBER,
                fontFamily: "var(--font-mono)",
                fontSize: "10px",
                letterSpacing: "0.04em",
                opacity: 0.55,
              }}
            >
              Split sides
            </button>
          </div>

          {missingBaseDuration && (
            <p style={missingDurationMessageStyle}>
              Add the hold time before saving, or clear this set.
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          {/* Left row */}
          <div className="flex items-center gap-2">
            <span style={sideTagStyle}>L</span>
            <NumberField
              ariaLabel={`Weight in ${load}, left`}
              value={weightForDisplay(set.weight_kg, load)}
              placeholder={load}
              step={String(weightStep)}
              onChange={(raw) => onSetField("weight_kg", raw)}
            />
            <span style={timesSignStyle}>×</span>
            {isSeconds ? (
              <NumberField
                ariaLabel="Hold (sec), left"
                value={set.duration_sec}
                placeholder="sec"
                step="1"
                onChange={(raw) => onSetField("duration_sec", raw)}
              />
            ) : (
              <NumberField
                ariaLabel="Reps, left"
                value={set.reps}
                placeholder="reps"
                step="1"
                onChange={(raw) => onSetField("reps", raw)}
              />
            )}
            {/* Same leftover-reps recovery as the unsplit branch above,
                for the left side's own `reps` value. */}
            {isSeconds && set.reps != null && (
              <>
                <span style={inheritedTagStyle}>reps</span>
                <NumberField
                  ariaLabel="Reps (inherited), left"
                  value={set.reps}
                  placeholder="reps"
                  step="1"
                  onChange={(raw) => onSetField("reps", raw)}
                />
              </>
            )}
          </div>

          {/* Right row */}
          <div className="flex items-center gap-2">
            <span style={sideTagStyle}>R</span>
            <NumberField
              ariaLabel={`Weight in ${load}, right`}
              value={weightForDisplay(set.weight_kg_right, load)}
              placeholder={load}
              step={String(weightStep)}
              onChange={(raw) => onSetField("weight_kg_right", raw)}
            />
            <span style={timesSignStyle}>×</span>
            {isSeconds ? (
              <NumberField
                ariaLabel="Hold (sec), right"
                value={set.duration_sec_right}
                placeholder="sec"
                step="1"
                onChange={(raw) => onSetField("duration_sec_right", raw)}
              />
            ) : (
              <NumberField
                ariaLabel="Reps, right"
                value={set.reps_right}
                placeholder="reps"
                step="1"
                onChange={(raw) => onSetField("reps_right", raw)}
              />
            )}
            {/* Same leftover-reps recovery as the unsplit branch above,
                for the right side's own `reps_right` value. */}
            {isSeconds && set.reps_right != null && (
              <>
                <span style={inheritedTagStyle}>reps</span>
                <NumberField
                  ariaLabel="Reps (inherited), right"
                  value={set.reps_right}
                  placeholder="reps"
                  step="1"
                  onChange={(raw) => onSetField("reps_right", raw)}
                />
              </>
            )}
          </div>

          {missingBaseDuration && (
            <p style={missingDurationMessageStyle}>
              Add the left side hold before saving, or clear the right side hold.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
